import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Button, Box, Typography, Alert, Snackbar, Chip, CircularProgress } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import { DataTable } from '../components/DataTable';
import { BeaconAssignmentForm, BeaconFormSubmitData } from '../components/beacon-assignments/BeaconAssignmentForm';
import { beaconAssignmentsApi } from '../api/services';
import { usePaginatedQuery } from '../hooks/usePaginatedQuery';
import { extractApiError } from '../utils/apiErrors';
import type { BeaconAssignment } from '../api/config';

export const BeaconAssignmentsPage: React.FC = () => {
  const [selectedAssignment, setSelectedAssignment] = React.useState<BeaconAssignment | undefined>();
  const [snackbar, setSnackbar] = React.useState<{ message: string; severity: 'success' | 'error' | 'warning' } | null>(null);
  const [isCreating, setIsCreating] = React.useState(false);

  const queryClient = useQueryClient();

  const emptyFilters = React.useMemo(() => ({} as Record<string, never>), []);

  const {
    rows: assignments,
    rowCount,
    isLoading: isLoadingAssignments,
    error: assignmentsError,
    paginationModel,
    onPaginationModelChange,
    sortModel,
    onSortModelChange,
    filterModel,
    onFilterModelChange,
  } = usePaginatedQuery({
    queryKey: ['beacon_assignments'],
    queryFn: (f, p) => beaconAssignmentsApi.getPage(f, p),
    filters: emptyFilters,
    defaultSort: { field: 'created_at', sort: 'desc' },
  });


  // Fetch all beacon IDs for client-side duplicate detection during bulk create.
  // Uses getAll (per_page=1000) so the check covers the full dataset, not just the current page.
  const { data: allAssignments = [] } = useQuery({
    queryKey: ['beacon_assignments_all_ids'],
    queryFn: () => beaconAssignmentsApi.getAll(),
  });
  const existingBeaconIds = React.useMemo(() => allAssignments.map(a => a.beacon_id), [allAssignments]);

  // Bulk create mutation
  const bulkCreateMutation = useMutation({
    mutationFn: beaconAssignmentsApi.bulkCreate,
    onSuccess: (data) => {
      const count = data.assignments.length;
      queryClient.invalidateQueries({ queryKey: ['beacon_assignments'] });
      if (data.errors?.length) {
        setSnackbar({ message: `${count} created, ${data.errors.length} failed`, severity: 'warning' });
      } else {
        setSnackbar({ message: `${count} tag assignment${count !== 1 ? 's' : ''} created successfully`, severity: 'success' });
      }
      handleClose();
    },
    onError: (error) => {
      setSnackbar({ message: extractApiError(error, 'Failed to create tag assignments'), severity: 'error' });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<BeaconAssignment> }) =>
      beaconAssignmentsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['beacon_assignments'] });
      setSnackbar({ message: 'Tag assignment updated successfully', severity: 'success' });
      handleClose();
    },
    onError: (error) => {
      setSnackbar({ message: extractApiError(error, 'Failed to update tag assignment'), severity: 'error' });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: beaconAssignmentsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['beacon_assignments'] });
      setSnackbar({ message: 'Tag assignment deleted successfully', severity: 'success' });
    },
    onError: (error) => {
      setSnackbar({ message: extractApiError(error, 'Failed to delete tag assignment'), severity: 'error' });
    },
  });

  const columns: GridColDef[] = [
    { field: 'beacon_id', headerName: 'Tag ID', width: 150 },
    { field: 'sku', headerName: 'SKU', width: 195 },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params: GridRenderCellParams<BeaconAssignment>) => (
        <Chip
          label={params.row?.status === 'active' ? 'Active' : 'Inactive'}
          color={params.row?.status === 'active' ? 'success' : 'default'}
          size="small"
        />
      ),
    },
  ];

  const handleClose = () => {
    setSelectedAssignment(undefined);
    setIsCreating(false);
    bulkCreateMutation.reset();
    updateMutation.reset();
  };

  const mutationError = bulkCreateMutation.error || updateMutation.error;

  const handleSubmit = (data: BeaconFormSubmitData) => {
    bulkCreateMutation.reset();
    updateMutation.reset();
    if (selectedAssignment) {
      updateMutation.mutate({
        id: selectedAssignment.beacon_id,
        data: { sku: data.sku, status: data.status as BeaconAssignment['status'] },
      });
    } else {
      const newAssignments = data.beaconIds.map(id => ({
        beacon_id: id,
        sku: data.sku,
        status: data.status as BeaconAssignment['status'],
      }));
      bulkCreateMutation.mutate(newAssignments);
    }
  };

  const handleEdit = (id: string) => {
    const assignment = assignments.find((a) => a.beacon_id === id);
    if (assignment) {
      setSelectedAssignment(assignment);
    }
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this tag assignment?')) {
      deleteMutation.mutate(id);
    }
  };

  if (assignmentsError) {
    return <Alert severity="error">Error loading tag assignments</Alert>;
  }

  return (
    <Box sx={{ height: '100%', width: '100%', p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">
          Tag Assignments
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setIsCreating(true)}
        >
          Add Tag Assignment
        </Button>
      </Box>
      
      {isLoadingAssignments ? (
        <CircularProgress />
      ) : (
        <>
          <DataTable
            rows={assignments}
            columns={columns}
            loading={isLoadingAssignments}
            onEditClick={handleEdit}
            onDeleteClick={handleDelete}
            getRowId={(row: BeaconAssignment) => row.beacon_id}
            fullHeight
            paginationMode="server"
            sortingMode="server"
            rowCount={rowCount}
            paginationModel={paginationModel}
            onPaginationModelChange={onPaginationModelChange}
            sortModel={sortModel}
            onSortModelChange={onSortModelChange}
            filterModel={filterModel}
            onFilterModelChange={onFilterModelChange}
            pageSizeOptions={[25, 50, 100]}
          />
          
          <BeaconAssignmentForm
            open={!!selectedAssignment || isCreating}
            onClose={handleClose}
            onSubmit={handleSubmit}
            initialData={selectedAssignment}
            existingBeaconIds={existingBeaconIds}
            error={mutationError ? extractApiError(mutationError) : undefined}
            isPending={bulkCreateMutation.isPending || updateMutation.isPending}
          />
          
          <Snackbar
            open={!!snackbar}
            autoHideDuration={6000}
            onClose={() => setSnackbar(null)}
          >
            <Alert severity={snackbar?.severity} onClose={() => setSnackbar(null)}>
              {snackbar?.message}
            </Alert>
          </Snackbar>
        </>
      )}
    </Box>
  );
};
